import { useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import { GET_APP_CONFIG } from '../apollo/queries/appConfig';

dayjs.extend(utc);
dayjs.extend(timezone);

const SOUTH_AFRICA_TIMEZONE = 'Africa/Johannesburg';
const TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)/;
const CONFIG_POLL_MS = 60_000;

function minutesOf(value: string | null | undefined): number | null {
    if (!value) {
        return null;
    }
    const match = TIME_PATTERN.exec(value.trim());
    if (!match) {
        return null;
    }
    return Number(match[1]) * 60 + Number(match[2]);
}

export function isSalesDataMaintenanceActive(
    startTime: string | null | undefined,
    endTime: string | null | undefined,
    now = new Date(),
): boolean {
    const startMinutes = minutesOf(startTime);
    const endMinutes = minutesOf(endTime);
    if (
        startMinutes == null ||
        endMinutes == null ||
        startMinutes === endMinutes
    ) {
        return false;
    }

    const nowInSouthAfrica = dayjs(now).tz(SOUTH_AFRICA_TIMEZONE);
    const nowMinutes = nowInSouthAfrica.hour() * 60 + nowInSouthAfrica.minute();
    if (startMinutes < endMinutes) {
        return nowMinutes >= startMinutes && nowMinutes < endMinutes;
    }
    return nowMinutes >= startMinutes || nowMinutes < endMinutes;
}

export function salesDataMaintenanceMessage(endsAtLabel: string): string {
    return `Sales data is under maintenance. We'll be back at ${endsAtLabel}. Please try again then.`;
}

export function useSalesDataMaintenance() {
    const { data } = useQuery(GET_APP_CONFIG, {
        pollInterval: CONFIG_POLL_MS,
    });
    const appConfig = data?.appConfig;
    const isActive = appConfig?.salesDataMaintenanceIsActive ?? false;
    const endsAt = appConfig?.salesDataMaintenanceEndsAt;
    const endsAtLabel = endsAt
        ? dayjs(endsAt).tz(SOUTH_AFRICA_TIMEZONE).format('HH:mm')
        : appConfig?.salesDataMaintenanceEndTime?.slice(0, 5) ?? '';

    return {
        isActive,
        endsAtLabel,
        message: isActive ? salesDataMaintenanceMessage(endsAtLabel) : '',
    };
}
